import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Popconfirm,
  Drawer,
  Select,
  Spin,
  Empty
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { api } from './api';

interface Group {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  user_count: number;
  admin_count: number;
  created_at: string;
  updated_at: string;
  members?: Member[];
}

interface Member {
  id: string;
  email: string;
  full_name: string | null;
  nickname: string | null;
  role: string;
  is_active: boolean;
  is_admin: boolean;
}

interface AllUser {
  id: string;
  email: string;
  nickname: string | null;
  full_name: string | null;
  role: string;
  is_active: boolean;
  group_id: string | null;
  group_name: string | null;
}

export default function AdminGroupsPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [addUserDrawerOpen, setAddUserDrawerOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<AllUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [addUserForm] = Form.useForm();

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/api/groups');
      if (!res.ok) throw new Error('Kunne ikke hente grupper');
      const data = await res.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente grupper');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGroupDetail = useCallback(async (groupId: string) => {
    setDetailLoading(true);
    try {
      const res = await api(`/api/groups/${groupId}`);
      if (!res.ok) throw new Error('Kunne ikke hente gruppe-detaljer');
      const data = await res.json();
      setSelectedGroup(data);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente gruppe-detaljer');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadAvailableUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await api('/api/groups/admin/users');
      if (!res.ok) throw new Error('Kunne ikke hente brugerliste');
      const data = await res.json();
      setAvailableUsers(Array.isArray(data) ? data : []);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke hente brugerliste');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleCreateGroup = async () => {
    try {
      const values = await createForm.validateFields();
      const res = await api('/api/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name.trim(),
          description: values.description ? values.description.trim() : null
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke oprette gruppe');
      }

      message.success('Gruppe oprettet');
      setCreateModalOpen(false);
      createForm.resetFields();
      await loadGroups();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.message || 'Kunne ikke oprette gruppe');
    }
  };

  const handleUpdateGroup = async () => {
    if (!selectedGroup) return;
    try {
      const values = await editForm.validateFields();
      const res = await api(`/api/groups/${selectedGroup.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name ? values.name.trim() : null,
          description: values.description ? values.description.trim() : null
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke opdatere gruppe');
      }

      message.success('Gruppe opdateret');
      setEditModalOpen(false);
      editForm.resetFields();
      await loadGroups();
      if (selectedGroup) {
        await loadGroupDetail(selectedGroup.id);
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.message || 'Kunne ikke opdatere gruppe');
    }
  };

  const handleActivateGroup = async (groupId: string) => {
    try {
      const res = await api(`/api/groups/${groupId}/activate`, {
        method: 'PATCH'
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke aktivere gruppe');
      }

      message.success('Gruppe aktiveret');
      await loadGroups();
      if (selectedGroup?.id === groupId) {
        await loadGroupDetail(groupId);
      }
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke aktivere gruppe');
    }
  };

  const handleDeactivateGroup = async (groupId: string) => {
    try {
      const res = await api(`/api/groups/${groupId}/deactivate`, {
        method: 'PATCH'
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke deaktivere gruppe');
      }

      message.success('Gruppe deaktiveret');
      await loadGroups();
      if (selectedGroup?.id === groupId) {
        await loadGroupDetail(groupId);
      }
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke deaktivere gruppe');
    }
  };

  const handleAddAdmin = async (userId: string) => {
    if (!selectedGroup) return;
    try {
      const res = await api(`/api/groups/${selectedGroup.id}/admins/${userId}`, {
        method: 'POST'
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke tildele gruppe-admin');
      }

      message.success('Gruppe-admin tildelt');
      await loadGroupDetail(selectedGroup.id);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke tildele gruppe-admin');
    }
  };

  const handleRemoveAdmin = async (userId: string) => {
    if (!selectedGroup) return;
    try {
      const res = await api(`/api/groups/${selectedGroup.id}/admins/${userId}`, {
        method: 'DELETE'
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke fjerne gruppe-admin');
      }

      message.success('Gruppe-admin fjernet');
      await loadGroupDetail(selectedGroup.id);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke fjerne gruppe-admin');
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!selectedGroup) return;
    try {
      const res = await api(`/api/groups/${selectedGroup.id}/users/${userId}`, {
        method: 'DELETE'
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke fjerne bruger fra gruppe');
      }

      message.success('Bruger fjernet fra gruppe');
      await loadGroupDetail(selectedGroup.id);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke fjerne bruger fra gruppe');
    }
  };

  const handleAddUserToGroup = async () => {
    if (!selectedGroup) return;
    try {
      const values = await addUserForm.validateFields();
      const res = await api(`/api/groups/${selectedGroup.id}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: values.userId })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Kunne ikke tilføje bruger til gruppe');
      }

      message.success('Bruger tilføjet til gruppe');
      setAddUserDrawerOpen(false);
      addUserForm.resetFields();
      await loadGroupDetail(selectedGroup.id);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.message || 'Kunne ikke tilføje bruger til gruppe');
    }
  };

  const handleOpenDetail = (group: Group) => {
    setSelectedGroup(group);
    setDetailDrawerOpen(true);
    loadGroupDetail(group.id);
  };

  const handleOpenEdit = (group: Group) => {
    setSelectedGroup(group);
    editForm.setFieldsValue({
      name: group.name,
      description: group.description || ''
    });
    setEditModalOpen(true);
  };

  const handleOpenAddUser = () => {
    loadAvailableUsers();
    setAddUserDrawerOpen(true);
  };

  const columns = [
    {
      title: 'Navn',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Beskrivelse',
      dataIndex: 'description',
      key: 'description',
      render: (text: string | null) => text || '—'
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (is_active: boolean) => (
        <Tag color={is_active ? 'green' : 'red'}>{is_active ? 'Aktiv' : 'Inaktiv'}</Tag>
      )
    },
    {
      title: 'Brugere',
      dataIndex: 'user_count',
      key: 'user_count'
    },
    {
      title: 'Admins',
      dataIndex: 'admin_count',
      key: 'admin_count'
    },
    {
      title: 'Handlinger',
      key: 'actions',
      width: 150,
      render: (_: any, record: Group) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<TeamOutlined />}
            onClick={() => handleOpenDetail(record)}
          >
            Detaljer
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleOpenEdit(record)}
          />
          <Popconfirm
            title="Slet gruppe?"
            description="Er du sikker?"
            onConfirm={() => {}}
            okText="Ja"
            cancelText="Nej"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Card title="Gruppestyring" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>Ny gruppe</Button>}>
      <Table
        columns={columns}
        dataSource={groups}
        loading={loading}
        rowKey="id"
        pagination={false}
      />

      <Modal
        title="Opret gruppe"
        open={createModalOpen}
        onOk={handleCreateGroup}
        onCancel={() => {
          setCreateModalOpen(false);
          createForm.resetFields();
        }}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="name"
            label="Gruppenavn"
            rules={[{ required: true, message: 'Gruppenavn er påkrævet' }]}
          >
            <Input placeholder="Fx. Administration" />
          </Form.Item>
          <Form.Item name="description" label="Beskrivelse">
            <Input.TextArea placeholder="Valgfri beskrivelse" rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Rediger gruppe"
        open={editModalOpen}
        onOk={handleUpdateGroup}
        onCancel={() => {
          setEditModalOpen(false);
          editForm.resetFields();
        }}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="name" label="Gruppenavn">
            <Input placeholder="Fx. Administration" />
          </Form.Item>
          <Form.Item name="description" label="Beskrivelse">
            <Input.TextArea placeholder="Valgfri beskrivelse" rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={selectedGroup ? `Gruppe: ${selectedGroup.name}` : 'Gruppe-detaljer'}
        onClose={() => {
          setDetailDrawerOpen(false);
          setSelectedGroup(null);
        }}
        open={detailDrawerOpen}
        width={600}
      >
        {detailLoading ? (
          <Spin />
        ) : selectedGroup ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <strong>Status:</strong>{' '}
              <Tag color={selectedGroup.is_active ? 'green' : 'red'}>
                {selectedGroup.is_active ? 'Aktiv' : 'Inaktiv'}
              </Tag>
            </div>
            <div>
              <strong>Beskrivelse:</strong> {selectedGroup.description || '—'}
            </div>
            <div>
              <strong>Brugere:</strong> {selectedGroup.user_count}
            </div>
            <div>
              <strong>Gruppe-admins:</strong> {selectedGroup.admin_count}
            </div>

            <Space style={{ width: '100%' }}>
              {!selectedGroup.is_active && selectedGroup.admin_count > 0 && (
                <Button type="primary" onClick={() => handleActivateGroup(selectedGroup.id)}>
                  Aktivér gruppe
                </Button>
              )}
              {selectedGroup.is_active && (
                <Button danger onClick={() => handleDeactivateGroup(selectedGroup.id)}>
                  Deaktivér gruppe
                </Button>
              )}
              <Button onClick={handleOpenAddUser}>Tilføj bruger</Button>
            </Space>

            <div style={{ marginTop: 24 }}>
              <h4>Medlemmer</h4>
              {selectedGroup.members && selectedGroup.members.length > 0 ? (
                <Table
                  dataSource={selectedGroup.members}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'Navn',
                      render: (_, record: Member) => record.full_name || record.email
                    },
                    {
                      title: 'Email',
                      dataIndex: 'email'
                    },
                    {
                      title: 'Rolle',
                      dataIndex: 'role'
                    },
                    {
                      title: 'Admin',
                      render: (_, record: Member) => (
                        <span>
                          {record.is_admin ? (
                            <Button
                              type="link"
                              size="small"
                              onClick={() => handleRemoveAdmin(record.id)}
                            >
                              Fjern
                            </Button>
                          ) : (
                            <Button
                              type="link"
                              size="small"
                              onClick={() => handleAddAdmin(record.id)}
                            >
                              Gør til admin
                            </Button>
                          )}
                        </span>
                      )
                    },
                    {
                      title: 'Handlinger',
                      render: (_, record: Member) => (
                        <Button
                          type="link"
                          size="small"
                          danger
                          onClick={() => handleRemoveUser(record.id)}
                        >
                          Fjern
                        </Button>
                      )
                    }
                  ]}
                />
              ) : (
                <Empty description="Ingen medlemmer" />
              )}
            </div>
          </Space>
        ) : null}
      </Drawer>

      <Drawer
        title="Tilføj bruger til gruppe"
        onClose={() => {
          setAddUserDrawerOpen(false);
          addUserForm.resetFields();
        }}
        open={addUserDrawerOpen}
        width={600}
      >
        <Form form={addUserForm} layout="vertical">
          <Form.Item
            name="userId"
            label="Bruger"
            rules={[{ required: true, message: 'Vælg en bruger' }]}
          >
            <Select
              placeholder="Vælg bruger"
              loading={usersLoading}
              options={availableUsers
                .filter((u) => !u.group_id)
                .map((u) => ({
                  value: u.id,
                  label: `${u.full_name || u.email} (${u.email})`
                }))}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" block onClick={handleAddUserToGroup}>
              Tilføj bruger
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
}
